// SPDX-License-Identifier: AGPL-3.0-or-later

import {Logger} from '@app/features/platform/utils/AppLogger';
import {getElectronAPI, isDesktop} from '@app/features/ui/utils/NativeUtils';
import type {VideoCodec} from 'livekit-client';

const logger = new Logger('NativeHardwareEncoderCapabilities');

export type NativeHardwareEncoderBackend = 'nvenc' | 'none';

export interface NativeHardwareEncoderCapabilities {
	available: boolean;
	backend: NativeHardwareEncoderBackend;
	compiled: boolean;
	runtime: boolean;
	codecs: Array<string>;
	reason?: string;
	detail?: string;
}

const UNAVAILABLE_CAPABILITIES: NativeHardwareEncoderCapabilities = {
	available: false,
	backend: 'none',
	compiled: false,
	runtime: false,
	codecs: [],
	reason: 'unavailable',
};

let cachedCapabilities: NativeHardwareEncoderCapabilities | null = null;
let pendingPromise: Promise<NativeHardwareEncoderCapabilities | null> | null = null;

function normalizeCapabilities(value: unknown): NativeHardwareEncoderCapabilities {
	if (typeof value !== 'object' || value === null) {
		return {...UNAVAILABLE_CAPABILITIES, reason: 'invalid-result'};
	}
	const candidate = value as Partial<NativeHardwareEncoderCapabilities>;
	const backend: NativeHardwareEncoderBackend = candidate.backend === 'nvenc' ? 'nvenc' : 'none';
	return {
		available: candidate.available === true,
		backend,
		compiled: candidate.compiled === true,
		runtime: candidate.runtime === true,
		codecs: Array.isArray(candidate.codecs) ? candidate.codecs.filter((codec) => typeof codec === 'string') : [],
		...(typeof candidate.reason === 'string' ? {reason: candidate.reason} : {}),
		...(typeof candidate.detail === 'string' ? {detail: candidate.detail} : {}),
	};
}

function fetchCapabilities(): Promise<NativeHardwareEncoderCapabilities | null> {
	if (!isDesktop()) return Promise.resolve(null);
	const bridge = getElectronAPI()?.voiceEngine;
	if (!bridge?.getHardwareEncoderCapabilities) return Promise.resolve(null);
	return bridge
		.getHardwareEncoderCapabilities()
		.then((capabilities) => {
			const normalized = normalizeCapabilities(capabilities);
			cachedCapabilities = normalized;
			return normalized;
		})
		.catch((error) => {
			logger.warn('Failed to query native hardware encoder capabilities', {error});
			cachedCapabilities = {...UNAVAILABLE_CAPABILITIES, reason: 'query-failed'};
			return cachedCapabilities;
		});
}

export function loadNativeHardwareEncoderCapabilities(): Promise<NativeHardwareEncoderCapabilities | null> {
	if (cachedCapabilities) return Promise.resolve(cachedCapabilities);
	if (pendingPromise) return pendingPromise;
	pendingPromise = fetchCapabilities().finally(() => {
		pendingPromise = null;
	});
	return pendingPromise;
}

export function getNativeHardwareEncoderCapabilitiesSync(): NativeHardwareEncoderCapabilities | null {
	return cachedCapabilities;
}

export function hasNativeNvencEncoder(codec: VideoCodec): boolean {
	const capabilities = getNativeHardwareEncoderCapabilitiesSync();
	if (!capabilities?.available || capabilities.backend !== 'nvenc') return false;
	if (codec !== 'h264' && codec !== 'h265') return false;
	const codecs = new Set(capabilities.codecs.map((entry) => entry.toLowerCase()));
	return codecs.has(codec) || (codec === 'h265' && codecs.has('hevc'));
}

export function resetNativeHardwareEncoderCapabilities(): void {
	cachedCapabilities = null;
	pendingPromise = null;
}
